import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, X, Copy, Check } from 'lucide-react';
import { AI } from '../lib/ai.js';
import { useAuth } from '../context/AuthContext.jsx';

// ---- System prompt ----
const BASE_SYSTEM = `You are VetTech AI, a concise exam-prep tutor for military veterans studying for IT certifications (CompTIA, AWS, Azure, GCP). Be direct, accurate, and use plain language. Keep answers under 200 words unless the user explicitly asks for more detail.

Formatting: you may use **bold**, \`inline code\`, fenced code blocks (\`\`\`), and bullet lists with "- ". Do not use headings, tables, or markdown links.`;

// Build the per-send system prompt with page context
function buildSystem(pageContext) {
  if (!pageContext || !pageContext.text) return BASE_SYSTEM;
  const truncated = pageContext.text.length > 3000
    ? pageContext.text.slice(0, 3000) + '…'
    : pageContext.text;
  return `${BASE_SYSTEM}\n\n---\nThe user is currently viewing: "${pageContext.title}" (${pageContext.path}).\nRelevant page content (for grounding — quote sparingly, do not regurgitate):\n${truncated}`;
}

// Capture visible text from the current page's main region
function capturePageContext(pathname) {
  if (typeof document === 'undefined') return null;
  const main = document.querySelector('main') || document.body;
  const text = (main?.innerText || '').replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  return {
    path: pathname,
    title: document.title || 'VetTech Learning Hub',
    text,
  };
}

// ---- Tiny safe markdown renderer ----
// Supports: **bold**, *italic*, `code`, ```fenced code blocks```, - bullet lists, 1. numbered lists, blank-line paragraphs.
// Escapes HTML; never sets innerHTML.
function renderInline(text, keyPrefix) {
  // Tokenize: code spans first (highest priority), then bold, then italic.
  const out = [];
  let i = 0;
  let buf = '';
  let key = 0;
  const flushText = () => {
    if (buf) {
      // bold + italic on plain runs
      const parts = [];
      let p = 0;
      let bufRun = buf;
      // **bold**
      bufRun.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).forEach((seg) => {
        if (!seg) return;
        if (seg.startsWith('**') && seg.endsWith('**') && seg.length > 4) {
          parts.push(<strong key={`${keyPrefix}-b${p++}`}>{seg.slice(2, -2)}</strong>);
        } else if (seg.startsWith('*') && seg.endsWith('*') && seg.length > 2) {
          parts.push(<em key={`${keyPrefix}-i${p++}`}>{seg.slice(1, -1)}</em>);
        } else {
          parts.push(seg);
        }
      });
      out.push(<span key={`${keyPrefix}-t${key++}`}>{parts}</span>);
      buf = '';
    }
  };
  while (i < text.length) {
    if (text[i] === '`') {
      // inline code
      const end = text.indexOf('`', i + 1);
      if (end > i) {
        flushText();
        out.push(<code key={`${keyPrefix}-c${key++}`} className="ai-md__code">{text.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  flushText();
  return out;
}

function renderMarkdown(text) {
  if (!text) return null;
  // Split on fenced code blocks first
  const parts = text.split(/```([\s\S]*?)```/g);
  const blocks = [];
  parts.forEach((part, idx) => {
    if (idx % 2 === 1) {
      // fenced code
      blocks.push(<pre key={`pre-${idx}`} className="ai-md__pre"><code>{part.replace(/^\n/, '')}</code></pre>);
      return;
    }
    // normal text — split into paragraph/list groups by blank lines
    const groups = part.split(/\n{2,}/);
    groups.forEach((group, gi) => {
      const trimmed = group.trim();
      if (!trimmed) return;
      const lines = trimmed.split('\n');
      const isBulletList = lines.every((l) => /^\s*-\s+/.test(l));
      const isNumList = lines.every((l) => /^\s*\d+\.\s+/.test(l));
      if (isBulletList) {
        blocks.push(
          <ul key={`ul-${idx}-${gi}`} className="ai-md__ul">
            {lines.map((l, li) => (
              <li key={li}>{renderInline(l.replace(/^\s*-\s+/, ''), `ul-${idx}-${gi}-${li}`)}</li>
            ))}
          </ul>
        );
      } else if (isNumList) {
        blocks.push(
          <ol key={`ol-${idx}-${gi}`} className="ai-md__ol">
            {lines.map((l, li) => (
              <li key={li}>{renderInline(l.replace(/^\s*\d+\.\s+/, ''), `ol-${idx}-${gi}-${li}`)}</li>
            ))}
          </ol>
        );
      } else {
        // paragraph — keep single-line breaks as <br/>
        blocks.push(
          <p key={`p-${idx}-${gi}`} className="ai-md__p">
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l, `p-${idx}-${gi}-${li}`)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      }
    });
  });
  return blocks;
}

// ---- Initial greeting message ----
const WELCOME = { id: 'welcome', role: 'ai', text: 'VETTECH AI ONLINE. Ask me anything about your cert exam — concepts, acronyms, practice questions. How can I help?' };

export default function AiPanel() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen]         = useState(false);
  const [msgs, setMsgs]         = useState([WELCOME]);
  const [input, setInput]       = useState('');
  const [streaming, setStreaming] = useState(false);
  const [usage, setUsage]       = useState(() => AI.getUsage());
  const [copiedId, setCopiedId] = useState(null);
  const configured               = AI.isConfigured();
  const abortRef                 = useRef(null);
  const bottomRef                = useRef(null);
  const textareaRef              = useRef(null);

  // Reset conversation on route change
  useEffect(() => {
    abortRef.current?.();
    setStreaming(false);
    setMsgs([WELCOME]);
    setInput('');
  }, [location.pathname]);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (open && configured) {
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [open, configured]);

  // Abort in-flight stream on unmount
  useEffect(() => () => abortRef.current?.(), []);

  function closePanel() {
    abortRef.current?.();
    setOpen(false);
  }

  function addMsg(msg) {
    setMsgs(prev => [...prev, { id: Date.now() + Math.random(), ...msg }]);
  }

  function updateLastAi(patch) {
    setMsgs(prev => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last?.role === 'ai') copy[copy.length - 1] = { ...last, ...patch };
      return copy;
    });
  }

  const sendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');

    // Add user bubble
    addMsg({ role: 'user', text });

    // Placeholder AI bubble (streaming)
    const aiId = Date.now();
    setMsgs(prev => [...prev, { id: aiId, role: 'ai', text: '', streaming: true }]);
    setStreaming(true);

    // Build message history for context (exclude welcome + system messages)
    const history = msgs
      .filter(m => m.role === 'user' || m.role === 'ai')
      .filter(m => m.id !== 'welcome')
      .map(m => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text }));
    history.push({ role: 'user', content: text });

    let aborted = false;
    abortRef.current = () => { aborted = true; };

    // Capture page context at send-time (not at mount-time — reflects current view)
    const pageContext = capturePageContext(location.pathname);

    AI.chat({
      system: buildSystem(pageContext),
      messages: history,
      onToken: (tok) => {
        if (aborted) return;
        setMsgs(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.id === aiId) copy[copy.length - 1] = { ...last, text: last.text + tok };
          return copy;
        });
      },
      onDone: () => {
        if (aborted) return;
        setMsgs(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.id === aiId) copy[copy.length - 1] = { ...last, streaming: false };
          return copy;
        });
        setStreaming(false);
        setUsage(AI.getUsage());
      },
      onError: (err) => {
        if (aborted) return;
        setMsgs(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.id === aiId) copy[copy.length - 1] = { ...last, text: err, streaming: false, error: true };
          return copy;
        });
        setStreaming(false);
      },
    });
  }, [input, streaming, msgs, location.pathname]);

  function handleKey(e) {
    // Enter sends; Shift+Enter adds newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleClear() {
    abortRef.current?.();
    setStreaming(false);
    setMsgs([WELCOME]);
    setUsage(AI.getUsage());
  }

  async function handleCopy(msg) {
    try {
      await navigator.clipboard.writeText(msg.text || '');
      setCopiedId(msg.id);
      setTimeout(() => setCopiedId((id) => (id === msg.id ? null : id)), 1500);
    } catch {
      /* clipboard unavailable — silent */
    }
  }

  // Only render for authenticated users
  if (!user) return null;

  return (
    <>
      {/* ---- FAB ---- */}
      <button
        className={`ai-fab${open ? ' ai-fab--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close AI chat' : 'Open AI chat'}
        title={open ? 'Close AI' : 'Ask VetTech AI'}
      >
        {open ? <X size={16} strokeWidth={2.5} /> : <Zap size={16} strokeWidth={2} />}
      </button>

      {/* ---- Backdrop + Panel ---- */}
      {open && (
        <>
          <div className="ai-backdrop" onClick={closePanel} />
          <div className="ai-panel" role="dialog" aria-label="VetTech AI chat">

            {/* Header */}
            <div className="ai-panel__head">
              <span className="ai-panel__title">VETTECH AI</span>
              <span className="ai-panel__badge">BYOK</span>
              {msgs.length > 1 && (
                <button
                  type="button"
                  className="ai-panel__close"
                  style={{ fontSize: '10px', letterSpacing: '0.1em', padding: '3px 8px', marginRight: '4px' }}
                  onClick={handleClear}
                  title="Clear conversation"
                >
                  CLR
                </button>
              )}
              <button type="button" className="ai-panel__close" onClick={closePanel} aria-label="Close"><X size={14} strokeWidth={2.5} /></button>
            </div>

            {/* Not configured */}
            {!configured ? (
              <div className="ai-panel__unconfigured">
                <span style={{ fontSize: '2rem' }}><Zap size={32} strokeWidth={1.5} /></span>
                <p>AI not configured.<br />Add your API key to unlock AI features.</p>
                <Link
                  to="/ai-settings"
                  className="btn btn-primary"
                  style={{ fontSize: '11px', padding: '.5rem 1.25rem' }}
                  onClick={closePanel}
                >
                  CONFIGURE AI →
                </Link>
              </div>
            ) : (
              <>
                {/* Message list */}
                <div className="ai-panel__msgs">
                  {msgs.map(m => (
                    <div
                      key={m.id}
                      className={`ai-bubble ai-bubble--${m.error ? 'err' : m.role}`}
                    >
                      <span className="ai-bubble__role">
                        {m.role === 'user' ? 'YOU' : m.role === 'ai' ? 'VETTECH AI' : ''}
                      </span>
                      <div className="ai-bubble__body">
                        {m.role === 'ai' && !m.error
                          ? <>{renderMarkdown(m.text)}{m.streaming && <span className="ai-bubble__cursor" />}</>
                          : <>{m.text || ''}{m.streaming && <span className="ai-bubble__cursor" />}</>}
                      </div>
                      {m.role === 'ai' && !m.error && !m.streaming && m.text && m.id !== 'welcome' && (
                        <button
                          type="button"
                          className="ai-bubble__copy"
                          onClick={() => handleCopy(m)}
                          title="Copy to clipboard"
                          aria-label="Copy response"
                        >
                          {copiedId === m.id
                            ? <><Check size={11} strokeWidth={2.5} /> COPIED</>
                            : <><Copy size={11} strokeWidth={2} /> COPY</>}
                        </button>
                      )}
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                {/* Token usage */}
                <div className="ai-panel__usage">
                  <span>SESSION</span>
                  <span>{usage.tokens.toLocaleString()} tokens · {usage.calls} calls</span>
                </div>

                {/* Input */}
                <div className="ai-panel__foot">
                  <textarea
                    ref={textareaRef}
                    className="ai-panel__input"
                    rows={1}
                    placeholder="Ask a question… (Enter to send)"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    disabled={streaming}
                  />
                  <button
                    type="button"
                    className="ai-panel__send"
                    onClick={sendMessage}
                    disabled={!input.trim() || streaming}
                  >
                    {streaming ? '…' : 'SEND'}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
